import { TextCodes } from '@api/types/translation/TextCodes';
import {
  CustomGoogleMap,
  DealerDetailsBrands,
  DealerDetailsShopTypes,
  LoadingSkeleton,
  StyledTextField,
  StyledTextarea,
  Title,
  useDealerDetailsContext,
} from '@components';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import { Box, Grid, styled } from '@mui/material';
import { LoadScriptProps, Marker, useJsApiLoader } from '@react-google-maps/api';
import { getSystemPages } from '@store/app/app.slice';
import { getDealer, getDealerPublicAddress } from '@store/dealer/dealer.slice';
import { colors } from '@themes/colors';
import { t } from '@translations';
import isNumber from 'lodash/isNumber';

import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';

const libraries: LoadScriptProps['libraries'] = ['places'];

const ShopInformationWrapper = styled(Box)`
  margin-top: 40px;
`;

export const DealerShopInformation = () => {
  const dealer = useSelector(getDealer);
  const dealerPublicAddress = useSelector(getDealerPublicAddress);
  const { isAuth, canEditData, form } = useDealerDetailsContext();
  const hasCoordinates = dealerPublicAddress?.latitude && dealerPublicAddress?.longitude;

  const systemPages = useSelector(getSystemPages);
  const googleMapApiKey = systemPages.googleMapApiKey as string;
  const { isLoaded, loadError } = useJsApiLoader({
    region: 'no',
    language: 'no',
    googleMapsApiKey: googleMapApiKey,
    libraries,
  });

  return (
    <ShopInformationWrapper>
      <Title variant='h4' noDot sx={{ mb: 2 }}>
        {t(TextCodes.Dealer.Details.ShopInformation)}
      </Title>

      <Grid container spacing={{ xs: 2, sm: 10 }}>
        <Grid item xs={12} sm={6}>
          {isAuth && (
            <>
              {canEditData ? (
                dealerPublicAddress?.alternativeName && (
                  <StyledTextField
                    {...form.register('shopAlternativeName')}
                    label={t(TextCodes.Dealer.Details.ShopName)}
                    fullWidth
                    sx={{
                      mt: 4,
                      display: dealerPublicAddress?.alternativeName ? 'block' : 'none',
                    }}
                    error={!!form.formState.errors.shopAlternativeName}
                    helperText={form.formState.errors.shopAlternativeName?.message}
                  />
                )
              ) : (
                <StyledTextField
                  label={t(TextCodes.Dealer.Details.ShopName)}
                  fullWidth
                  sx={{
                    mt: 4,
                    display: dealerPublicAddress?.alternativeName ? 'block' : 'none',
                  }}
                  disabled
                  inputProps={{ defaultValue: dealerPublicAddress?.alternativeName ?? '' }}
                />
              )}

              {canEditData ? (
                <StyledTextField
                  {...form.register('shopName')}
                  label={t(TextCodes.Dealer.Details.ShopName)}
                  fullWidth
                  sx={{
                    mt: 4,
                    display: dealerPublicAddress?.alternativeName ? 'none' : 'block',
                  }}
                  error={!!form.formState.errors.shopName}
                  helperText={form.formState.errors.shopName?.message}
                />
              ) : (
                dealerPublicAddress?.name && (
                  <StyledTextField
                    label={t(TextCodes.Dealer.Details.ShopName)}
                    fullWidth
                    sx={{
                      mt: 4,
                      display: dealerPublicAddress?.alternativeName ? 'none' : 'block',
                    }}
                    disabled
                    inputProps={{ defaultValue: dealerPublicAddress?.name ?? '' }}
                  />
                )
              )}
            </>
          )}

          {dealerPublicAddress?.addressType && (
            <StyledTextField
              defaultValue={dealerPublicAddress?.line1}
              label={t(TextCodes.Dealer.Details.Address, {
                address: dealerPublicAddress?.addressType,
              })}
              sx={{ mt: 4 }}
              fullWidth
              disabled
            />
          )}

          <Grid container spacing={2}>
            <Grid item xs={3}>
              {dealerPublicAddress?.postalCode && (
                <StyledTextField
                  defaultValue={dealerPublicAddress?.postalCode}
                  label={t(TextCodes.Dealer.Details.PostalCode)}
                  sx={{ mt: 4 }}
                  fullWidth
                  disabled
                />
              )}
            </Grid>
            <Grid item xs={9}>
              {dealerPublicAddress?.city && (
                <StyledTextField
                  defaultValue={dealerPublicAddress?.city}
                  label={t(TextCodes.Dealer.Details.City)}
                  sx={{ mt: 4 }}
                  fullWidth
                  disabled
                />
              )}
            </Grid>
          </Grid>

          {isNumber(hasCoordinates) && isLoaded && (
            <Box sx={{ mt: 4 }}>
              <CustomGoogleMap
                center={{ lat: dealer?.latitude || 0, lng: dealer?.longitude || 0 }}
                zoom={16}
                mapContainerStyle={{ height: '415px', width: '100%' }}
              >
                <Marker
                  position={{
                    lat: dealer?.latitude || 0,
                    lng: dealer?.longitude || 0,
                  }}
                />
              </CustomGoogleMap>
            </Box>
          )}
          {(!isNumber(hasCoordinates) || loadError) && <NoLocationBox />}
          {!isLoaded && (
            <LoadingSkeleton variant='rectangular' width='100%' height={300} sx={{ mt: 2 }} />
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          {canEditData || form.watch('workingHours') ? (
            <Controller
              name='workingHours'
              control={form.control}
              render={({ field, formState: { errors } }) => (
                <StyledTextarea
                  sx={{ mt: 4 }}
                  {...field}
                  label={t(TextCodes.Dealer.Details.WorkingHours)}
                  fullWidth
                  multiline
                  maxRows={10}
                  InputProps={{ readOnly: !canEditData }}
                  error={!!errors.workingHours}
                  helperText={errors.workingHours?.message}
                />
              )}
            />
          ) : null}

          <DealerDetailsShopTypes />
          <DealerDetailsBrands />
        </Grid>
      </Grid>
    </ShopInformationWrapper>
  );
};

const NoLocationBox = () => {
  return (
    <Box
      bgcolor={colors.background.disabled}
      height='415px'
      width='100%'
      display='flex'
      alignItems='center'
      justifyContent='center'
      mt={4}
    >
      <LocationOffIcon color='error' fontSize='large' />
      {t(TextCodes.Dealer.Details.GoogleMapsCoordinatesProblem)}
    </Box>
  );
};
